'use client';

import type { PostgrestClientOptions, PostgrestQueryBuilder } from '@supabase/postgrest-js';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  type DefaultError,
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';
import { useMemo } from 'react';
import { getSupabaseClient } from './client';
import type { QueryDefMany, QueryDefOne } from './queryDef';
import { DEFAULT_PAGE_SIZE, DEFAULT_STALE_TIME, resolveColumns } from './queryDef';

// ============ Type System ============

const supabase = getSupabaseClient();
type SupabaseClientType = typeof supabase;

// Utility type to check if the type is any
type IfAny<T, Y, N> = 0 extends 1 & T ? Y : N;

// Extracts the database type from the supabase client
type Database =
  SupabaseClientType extends SupabaseClient<infer U>
    ? IfAny<
        U,
        {
          public: {
            Tables: Record<string, { Row: Record<string, unknown> }>;
            Views: Record<string, unknown>;
            Functions: Record<string, unknown>;
          };
        },
        U
      >
    : {
        public: {
          Tables: Record<string, { Row: Record<string, unknown> }>;
          Views: Record<string, unknown>;
          Functions: Record<string, unknown>;
        };
      };

type DatabaseSchema = Database['public'];
type DefaultClientOptions = PostgrestClientOptions;

/** Available table names in the database */
export type TableName = keyof DatabaseSchema['Tables'];

/** Row type for a given table */
export type TableRow<T extends TableName> = DatabaseSchema['Tables'][T]['Row'];

/** Select builder type for query modifications */
type SelectBuilder<T extends TableName> = ReturnType<
  PostgrestQueryBuilder<
    DefaultClientOptions,
    DatabaseSchema,
    DatabaseSchema['Tables'][T],
    T
  >['select']
>;

// ============ useOne ============

interface UseOneOptions<T extends TableName, TRow, TDomain> {
  /** Table name to query */
  tableName: T;
  /** Columns to select (default: '*') */
  columns?: string;
  /** React Query cache key */
  key: readonly unknown[];
  /** Whether the query is enabled (default: true) */
  enabled?: boolean;
  /** Time in ms that data is considered fresh (default: 30s) */
  staleTime?: number;
  /** Chain query methods after select (filter, order, limit, single, etc.) */
  trailingQuery: (query: SelectBuilder<T>) => ReturnType<SelectBuilder<T>['single']>;
  /** Transform Row to Domain type. For joins, specify row type in parameter. */
  parseFn: (data: TRow) => TDomain;
  /** Whether to throw errors to error boundary (default: false) */
  throwOnError?: boolean;
}

export function useOne<T extends TableName, TRow, TDomain>({
  tableName,
  columns = '*',
  key,
  enabled = true,
  staleTime = DEFAULT_STALE_TIME,
  trailingQuery,
  parseFn,
  throwOnError = false,
}: UseOneOptions<T, TRow, TDomain>) {
  return useQuery<TRow, DefaultError, TDomain>({
    queryKey: key,
    staleTime,
    queryFn: async () => {
      const query = getSupabaseClient()
        .from(tableName)
        .select(columns) as unknown as SelectBuilder<T>;

      const finalQuery = trailingQuery(query);
      const { data, error } = await finalQuery;

      if (error) {
        throw error;
      }
      if (data === null) {
        throw new Error('Not found');
      }
      return data as TRow;
    },
    select: parseFn ? data => parseFn(data) : undefined,
    enabled,
    throwOnError,
  });
}

// ============ useMany ============

interface UseManyOptions<T extends TableName, TRow, TDomain> {
  /** Table name to query */
  tableName: T;
  /** Columns to select (default: '*') */
  columns?: string;
  /** React Query cache key */
  key: readonly unknown[];
  /** Whether the query is enabled (default: true) */
  enabled?: boolean;
  /** Time in ms that data is considered fresh (default: 30s) */
  staleTime?: number;
  /** Number of items per page (default: 20) */
  pageSize?: number;
  /** Chain query methods after select (filter, order, etc.). Range is applied automatically. */
  trailingQuery?: (query: SelectBuilder<T>) => SelectBuilder<T>;
  /** Transform Row to Domain type. For joins, specify row type in parameter. */
  parseFn: (data: TRow) => TDomain;
  /** Whether to throw errors to error boundary (default: false) */
  throwOnError?: boolean;
}

export function useMany<T extends TableName, TRow, TDomain>({
  tableName,
  columns = '*',
  key,
  enabled = true,
  staleTime = DEFAULT_STALE_TIME,
  pageSize = DEFAULT_PAGE_SIZE,
  trailingQuery,
  parseFn,
  throwOnError = false,
}: UseManyOptions<T, TRow, TDomain>) {
  return useInfiniteQuery({
    queryKey: key,
    staleTime,
    queryFn: async ({ pageParam }) => {
      const from = pageParam * pageSize;
      const to = from + pageSize - 1;

      let query = getSupabaseClient()
        .from(tableName)
        .select(columns) as unknown as SelectBuilder<T>;

      if (trailingQuery) {
        query = trailingQuery(query);
      }

      const { data, error } = await query.range(from, to);

      if (error) {
        throw error;
      }
      return (data ?? []) as TRow[];
    },
    select: data => data.pages.flatMap(page => page.map(parseFn)),
    enabled,
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.length < pageSize) {
        return undefined;
      }
      return allPages.length;
    },
    throwOnError,
  });
}

// ============ usePaginated ============

interface UsePaginatedOptions<T extends TableName, TRow, TDomain> {
  /** Table name to query */
  tableName: T;
  /** Columns to select (default: '*') */
  columns?: string;
  /** React Query cache key (page will be appended) */
  key: readonly unknown[];
  /** Whether the query is enabled (default: true) */
  enabled?: boolean;
  /** Time in ms that data is considered fresh (default: 30s) */
  staleTime?: number;
  /** Current page number (1-indexed) */
  page: number;
  /** Number of items per page (default: 20) */
  pageSize?: number;
  /** Chain query methods after select (filter, order, etc.). Range is applied automatically. */
  trailingQuery?: (query: SelectBuilder<T>) => SelectBuilder<T>;
  /** Transform Row to Domain type. For joins, specify row type in parameter. */
  parseFn: (data: TRow) => TDomain;
}

interface UsePaginatedResult<TDomain> {
  data: TDomain[];
  totalCount: number;
  totalPages: number;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
}

export function usePaginated<T extends TableName, TRow, TDomain>({
  tableName,
  columns = '*',
  key,
  enabled = true,
  staleTime = DEFAULT_STALE_TIME,
  page,
  pageSize = DEFAULT_PAGE_SIZE,
  trailingQuery,
  parseFn,
}: UsePaginatedOptions<T, TRow, TDomain>): UsePaginatedResult<TDomain> {
  const result = useQuery({
    queryKey: key,
    staleTime,
    queryFn: async () => {
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;

      let query = getSupabaseClient()
        .from(tableName)
        .select(columns, { count: 'exact' }) as unknown as SelectBuilder<T>;

      if (trailingQuery) {
        query = trailingQuery(query);
      }

      const { data, error, count } = await query.range(from, to);

      if (error) {
        throw error;
      }

      return {
        data: (data ?? []) as TRow[],
        totalCount: count ?? 0,
      };
    },
    enabled,
    throwOnError: true,
    placeholderData: keepPreviousData,
  });

  const totalCount = result.data?.totalCount ?? 0;

  const data = useMemo(() => {
    const rawData = result.data?.data ?? [];
    return rawData.map(parseFn);
  }, [result.data?.data, parseFn]);

  return {
    data,
    totalCount,
    totalPages: Math.ceil(totalCount / pageSize),
    isLoading: result.isLoading,
    isFetching: result.isFetching,
    error: result.error,
  };
}

// ============ QueryDef-based hooks ============

interface UseManyWithDefOptions {
  enabled?: boolean;
  pageSize?: number;
  staleTime?: number;
  throwOnError?: boolean;
}

/**
 * useMany with QueryDef - eliminates filter logic duplication
 *
 * @example
 * ```ts
 * // In queryDef.ts
 * export const activeMissionsQueryDef: QueryDefMany<...> = { ... };
 *
 * // In hook
 * export function useActiveMissions() {
 *   const now = useMemo(() => new Date().toISOString(), []);
 *   return useManyWithDef(activeMissionsQueryDef, { now });
 * }
 * ```
 */
export function useManyWithDef<T extends TableName, TRow, TDomain, TParams>(
  def: QueryDefMany<T, TRow, TDomain, TParams>,
  params: TParams,
  options: UseManyWithDefOptions = {},
) {
  const {
    enabled = true,
    pageSize = def.pageSize ?? DEFAULT_PAGE_SIZE,
    staleTime = def.staleTime ?? DEFAULT_STALE_TIME,
    throwOnError = false,
  } = options;

  const columns = resolveColumns(def.columns, params);

  return useInfiniteQuery({
    queryKey: def.queryKey(params),
    staleTime,
    queryFn: async ({ pageParam }) => {
      const from = pageParam * pageSize;
      const to = from + pageSize - 1;

      let query = getSupabaseClient()
        .from(def.tableName)
        .select(columns) as unknown as SelectBuilder<T>;

      query = def.trailingQuery(query, params);

      const { data, error } = await query.range(from, to);

      if (error) {
        throw error;
      }
      return (data ?? []) as TRow[];
    },
    select: data => data.pages.flatMap(page => page.map(def.parseFn)),
    enabled,
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.length < pageSize) {
        return undefined;
      }
      return allPages.length;
    },
    throwOnError,
  });
}

interface UseOneWithDefOptions {
  enabled?: boolean;
  staleTime?: number;
  throwOnError?: boolean;
}

/**
 * useOne with QueryDef - eliminates filter logic duplication
 */
export function useOneWithDef<T extends TableName, TRow, TDomain, TParams>(
  def: QueryDefOne<T, TRow, TDomain, TParams>,
  params: TParams,
  options: UseOneWithDefOptions = {},
) {
  const {
    enabled = true,
    staleTime = def.staleTime ?? DEFAULT_STALE_TIME,
    throwOnError = false,
  } = options;

  const columns = resolveColumns(def.columns, params);

  return useQuery<TRow, DefaultError, TDomain>({
    queryKey: def.queryKey(params),
    staleTime,
    queryFn: async () => {
      const query = getSupabaseClient()
        .from(def.tableName)
        .select(columns) as unknown as SelectBuilder<T>;

      const finalQuery = def.trailingQuery(query, params);
      const { data, error } = await finalQuery;

      if (error) {
        throw error;
      }
      if (data === null) {
        throw new Error('Not found');
      }
      return data as TRow;
    },
    select: data => def.parseFn(data),
    enabled,
    throwOnError,
  });
}

// ============ Exports ============

export type {
  SelectBuilder,
  UseOneOptions,
  UseManyOptions,
  UsePaginatedOptions,
  UsePaginatedResult,
};

// Re-export QueryDef types for convenience
export type { QueryDefMany, QueryDefOne } from './queryDef';
export { resolveColumns } from './queryDef';
